import { useEffect } from "react";

// Same approach as homepage-new/seo.js: small imperative head management, no
// react-helmet. Cohort structured data is rebuilt whenever the API answers, so
// a new batch shows up in search results without a code change.
const SITE = "https://edgent.in";
const TITLE = "Reinforce — live, cohort-based AI & data science coaching";
const DESCRIPTION =
  "Live, cohort-based AI engineering and data science coaching. Small batches, fixed weeks, and the full syllabus published before you pay.";

const upsertMeta = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
};

const upsertLink = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const SCHEMA_ID = "reinforce-schema";

export const useReinforceSeo = (cohorts = []) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = TITLE;
    upsertMeta('meta[name="description"]', { name: "description", content: DESCRIPTION });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: TITLE });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: DESCRIPTION });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: `${SITE}/reinforce` });
    upsertLink("canonical", `${SITE}/reinforce`);
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!cohorts.length) return undefined;

    const courses = cohorts.map((c) => ({
      "@context": "https://schema.org",
      "@type": "Course",
      name: c.title,
      description: c.description,
      provider: { "@type": "Organization", name: "Reinforce", sameAs: `${SITE}/reinforce` },
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        startDate: c.start_date,
        endDate: c.end_date,
        courseWorkload: `PT${c.weekly_hours || 6}H`,
      },
      offers: {
        "@type": "Offer",
        price: c.price_discounted || c.price_actual,
        priceCurrency: c.currency || "INR",
        availability: c.status === "open" || c.status === "closing_soon" ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      },
    }));

    let script = document.getElementById(SCHEMA_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCHEMA_ID;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(courses);

    return () => {
      document.getElementById(SCHEMA_ID)?.remove();
    };
  }, [cohorts]);
};

export default useReinforceSeo;
